import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, siteUrl } from "@/lib/schemas";
import { useAuth } from "@/hooks/useAuth";
import { Eye, EyeOff, Mail, Lock, ArrowRight, Shield, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/login`;

  const schemas = [
    webpageSchema(
      "Login | ExpertAdvise.in Customer Portal",
      "Login to your ExpertAdvise.in account to view your insurance recommendations, appointment history, and claims status.",
      pageUrl
    ),
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error: authError, user: returnedUser } = await signIn(email, password);
    setLoading(false);

    if (authError) {
      setError(authError);
      return;
    }

    if (returnedUser?.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Login | ExpertAdvise.in Customer Portal"
        description="Login to your ExpertAdvise.in account to view your insurance recommendations, appointment history, and claims status."
        canonical={pageUrl}
        schemas={schemas}
        noindex
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="max-w-md mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-primary-600" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">Welcome Back</h1>
              <p className="text-sm text-slate-500 mt-1">Sign in to your ExpertAdvise account</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                  <Mail className="w-4 h-4 text-primary-500" />
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                  <Lock className="w-4 h-4 text-primary-500" />
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cta-600 hover:bg-cta-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>

            <div className="mt-4 p-3 bg-slate-50 rounded-lg space-y-1">
              <p className="text-xs text-slate-500 text-center font-medium">
                <strong>Admin 1:</strong> mohit@expertadvise.in / 123456
              </p>
              <p className="text-xs text-slate-500 text-center font-medium">
                <strong>Admin 2:</strong> raj@expertadvise.in / 123456
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 text-center">
              <p className="text-sm text-slate-500">
                Don&apos;t have an account?{" "}
                <Link to="/register" className="text-primary-600 font-semibold hover:text-primary-700">
                  Create one
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}